import { AnalyticsEvent } from './analytics.types';

declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
    gtag?: (...args: any[]) => void;
  }
}

export interface PropertyAnalyticsConfig {
  googleAnalyticsId?: string | null;
  gaid?: string | null; // Legacy GA property
  googleTagManager?: string | null;
}

function createAnalytics() {
  let enabled = true;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function gtag(...args: any[]) {
    if (typeof window !== 'undefined') {
      window.dataLayer ??= [];

      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    }
  }

  function getEffectiveGoogleAnalyticsId(propertyConfig: PropertyAnalyticsConfig): string | null {
    // Priority order: googleAnalyticsId (GA4) > gaid (legacy) > null
    return propertyConfig.googleAnalyticsId || propertyConfig.gaid || null;
  }

  function track(event: AnalyticsEvent, propertyConfig?: PropertyAnalyticsConfig) {
    if (!enabled) {
      return;
    }

    const effectiveGA4Id = propertyConfig ? getEffectiveGoogleAnalyticsId(propertyConfig) : null;
    if (effectiveGA4Id) {
      gtag('config', effectiveGA4Id);
    }

    gtag('event', event.event, event.params);
  }

  function trackToProperty(event: AnalyticsEvent, propertyConfig: PropertyAnalyticsConfig) {
    const effectiveGA4Id = getEffectiveGoogleAnalyticsId(propertyConfig);

    if (!enabled || !effectiveGA4Id) {
      return;
    }

    gtag('event', event.event, {
      ...event.params,
      send_to: effectiveGA4Id,
    });
  }

  function trackPurchase(event: AnalyticsEvent, propertyConfig: PropertyAnalyticsConfig) {
    if (!enabled) {
      return;
    }

    const effectiveGA4Id = getEffectiveGoogleAnalyticsId(propertyConfig);

    if (effectiveGA4Id) {
      trackToProperty(event, propertyConfig);
    }

    if (typeof window !== 'undefined' && window.dataLayer && propertyConfig.googleTagManager) {
      window.dataLayer.push({
        event: 'ga4_purchase',
        ecommerce: {
          ...event.params,
        },
        gtm_container_id: propertyConfig.googleTagManager,
        ga4_measurement_id: effectiveGA4Id,
      });
    }
  }

  function disableTracking() {
    enabled = false;
  }

  function enableTracking() {
    enabled = true;
  }

  return {
    track,
    trackToProperty,
    trackPurchase,
    enableTracking,
    disableTracking,
    gtag,
  };
}

export const analytics = createAnalytics();

export function getPropertyAnalyticsConfig(property: any): PropertyAnalyticsConfig {
  if (!property) {
    return {
      googleAnalyticsId: null,
      gaid: null,
      googleTagManager: null,
    };
  }

  return {
    googleAnalyticsId: property.googleAnalyticsId || null,
    gaid: property.gaid || null,
    googleTagManager: property.googleTagManager || null,
  };
}

export function trackPurchaseEvent(
  order: any,
  property: any,
  source: 'payment_success' | 'confirmation_page'
): void {
  if (!order?.total || !order?.items?.length || !property) {
    return;
  }

  const propertyConfig = getPropertyAnalyticsConfig(property);

  const purchaseEvent = {
    event: 'purchase' as const,
    source,
    params: {
      currency: order.total.currencyCode,
      transaction_id: order.orderReference,
      value: order.total.amount / 100,
      items: order.items.map((item) => ({
        item_id:
          item.id || item.bookable?.ids?.rateId || item.bookable?.ids?.externalId || 'unknown',
        item_name: item.name || item.title || item.bookable?.name || 'Unknown Item',
        item_category: item.type,
        quantity: item.quantity || 1,
        price: (item.price?.amount || 0) / 100,
      })),
    },
  };

  analytics.trackPurchase(purchaseEvent, propertyConfig);
}
